import React from 'react';

/**
 * Portrait cut carousel used during video generation (studio / onboarding).
 * Expects items with `_id` and `url`.
 */
export default function SmoothScaleCarousel({ items = [], compact = false }) {
  const N = items.length;

  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 768px)').matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const copies = N <= 1 ? 1 : (isMobile ? 1 : 2);
  const extendedItems = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < copies; i++) {
      result.push(...items);
    }
    return result;
  }, [items, copies]);

  const [index, setIndex] = React.useState(Math.max(0, Math.floor(N / 2)));
  const [resting, setResting] = React.useState(true);
  const dwell = compact ? 1000 : 1200;
  const duration = compact ? 700 : 900;
  const videoRefs = React.useRef({});

  React.useEffect(() => {
    setIndex(Math.max(0, Math.floor(N / 2)));
  }, [N]);

  React.useEffect(() => {
    if (!resting || N === 0) return undefined;
    const id = setTimeout(() => {
      setResting(false);
      setIndex((i) => i + 1);
    }, dwell);
    return () => clearTimeout(id);
  }, [resting, dwell, N]);

  React.useEffect(() => {
    if (isMobile) return;
    const PLAY_RANGE = compact ? 1 : 2;
    Object.keys(videoRefs.current).forEach((key) => {
      const video = videoRefs.current[key];
      if (!video) return;
      const videoIndex = parseInt(key.split('-')[0], 10);
      const distanceFromCenter = Math.abs(videoIndex - index);
      try {
        if (distanceFromCenter <= PLAY_RANGE) {
          if (video.paused) video.play().catch(() => {});
        } else if (!video.paused) {
          video.pause();
        }
      } catch (_) {
        /* ignore */
      }
    });
  }, [index, isMobile, compact]);

  const onTransitionEnd = () => {
    setResting(true);
    if (copies === 1) {
      if (index >= N) setIndex(0);
    } else if (index >= N * 2) {
      setIndex(N);
    }
  };

  const VISIBLE = 3;
  const STEP = 100 / VISIBLE;
  const translate = STEP - index * STEP;
  const trackWidth = (extendedItems.length / VISIBLE) * 100;
  const cardRadius = compact ? '14px' : '16px';

  if (N === 0) return null;

  return (
    <div style={{ overflowX: 'hidden', width: compact ? '100%' : undefined, height: compact ? '100%' : undefined }}>
      <style>{`
        @keyframes carousel-green-scan {
          0% { top: -48px; }
          28.57% { top: 100%; }
          57.14% { top: -48px; }
          85.71% { top: 100%; }
          100% { top: 100%; }
        }
        .carousel-scan-overlay {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          overflow: hidden;
          pointer-events: none;
          z-index: 3;
        }
        .carousel-scan-band {
          position: absolute;
          left: 0;
          right: 0;
          height: 48px;
          background: linear-gradient(
            to bottom,
            rgba(0, 255, 136, 0),
            rgba(0, 255, 160, 0.35),
            rgba(100, 255, 180, 0.25),
            rgba(0, 255, 136, 0)
          );
          animation: carousel-green-scan 3.5s ease-in-out infinite;
        }
      `}</style>
      <div
        style={{
          overflowY: 'visible',
          maxWidth: compact ? '100%' : 800,
          margin: '0 auto',
          position: 'relative',
          width: compact ? '100%' : undefined,
          height: compact ? '100%' : 440,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: compact ? 0 : 30,
        }}
      >
        <div
          onTransitionEnd={onTransitionEnd}
          style={{
            display: 'flex',
            transition: `transform ${duration}ms cubic-bezier(0.4,0,0.2,1)`,
            transform: `translateX(${translate}%)`,
            width: `${trackWidth}%`,
            height: compact ? '100%' : undefined,
            alignItems: 'center',
          }}
        >
          {extendedItems.map((cut, i) => {
            const isCenter = i === index;
            const scale = isCenter ? (compact ? 1 : 1.15) : (compact ? 0.72 : 0.85);
            const opacity = isCenter ? 1 : (compact ? 0.5 : 0.7);
            const distanceFromCenter = Math.abs(i - index);
            const shouldPlay = !isMobile && distanceFromCenter <= (compact ? 1 : 2);

            return (
              <div
                key={`${i}-${cut._id}`}
                style={{
                  flex: `0 0 ${STEP}%`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: compact ? '100%' : undefined,
                  transition: 'transform 600ms ease, opacity 600ms ease',
                  transform: `scale(${scale})`,
                  opacity,
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    ...(compact
                      ? { height: '94%', width: 'auto' }
                      : { width: '55%' }),
                    aspectRatio: '9 / 16',
                    borderRadius: cardRadius,
                    overflow: 'hidden',
                    backgroundColor: 'var(--background-primary, #111)',
                    boxShadow: isCenter
                      ? (compact ? '0 8px 20px rgba(0,0,0,0.32)' : '0 20px 40px rgba(0,0,0,0.35)')
                      : (compact ? '0 3px 8px rgba(0,0,0,0.18)' : '0 10px 20px rgba(0,0,0,0.2)'),
                  }}
                >
                  <video
                    ref={(el) => {
                      if (el) videoRefs.current[`${i}-${cut._id}`] = el;
                    }}
                    src={cut.url}
                    playsInline
                    muted
                    autoPlay={shouldPlay}
                    loop={shouldPlay}
                    preload={shouldPlay ? 'auto' : 'metadata'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                    onLoadedMetadata={(e) => {
                      try {
                        e.currentTarget.currentTime = e.currentTarget.duration / 2;
                      } catch (_) {
                        /* ignore */
                      }
                    }}
                  />
                  <div className="carousel-scan-overlay" aria-hidden="true">
                    <div className="carousel-scan-band" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
